import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Paper, CircularProgress, Alert, FormControl, InputLabel,
  Select, MenuItem, TextField, Button, Chip, Collapse, IconButton, TablePagination,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import { format } from 'date-fns';
import { dmsApi } from '../services/dmsApi';
import useDmsPalette from '../hooks/useDmsPalette';

const ACTIONS = [
  'upload', 'download', 'view', 'edit', 'delete', 'share',
  'permission_change', 'workflow_action', 'login', 'export',
];

const ACTION_COLORS = {
  upload: 'info', download: 'default', view: 'default', edit: 'warning',
  delete: 'error', share: 'secondary', permission_change: 'warning',
  workflow_action: 'primary', login: 'success', export: 'info',
};

function AuditRow({ log }) {
  const MP = useDmsPalette();
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover sx={{ '&:hover': { bgcolor: `${MP.blue}08` } }}>
        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
          {format(new Date(log.timestamp || log.createdAt), 'MMM d, yyyy HH:mm:ss')}
        </TableCell>
        <TableCell sx={{ fontSize: '0.8rem' }}>
          {log.userName || (typeof log.userId === 'object' ? (log.userId?.name || log.userId?.email) : log.userId) || (typeof log.performedBy === 'object' ? (log.performedBy?.name || log.performedBy?.email) : log.performedBy) || '—'}
        </TableCell>
        <TableCell>
          <Chip label={log.action} size="small" color={ACTION_COLORS[log.action] || 'default'} sx={{ fontSize: '0.7rem' }} />
        </TableCell>
        <TableCell sx={{ fontSize: '0.8rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {log.documentTitle || (typeof log.documentId === 'object' ? (log.documentId?.title || log.documentId?.originalName) : log.documentId) || '—'}
        </TableCell>
        <TableCell sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{log.ipAddress || log.ip || '—'}</TableCell>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(o => !o)}>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5, px: 1 }}>
              <Typography variant="caption" color="text.secondary" component="pre" sx={{
                fontFamily: 'monospace', fontSize: '0.72rem', bgcolor: MP.black, p: 1.5,
                borderRadius: 1, overflow: 'auto', maxHeight: 200, whiteSpace: 'pre-wrap',
              }}>
                {JSON.stringify(log.details || log, null, 2)}
              </Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function AdminAudit() {
  const MP = useDmsPalette();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: page + 1, limit: rowsPerPage };
      if (actionFilter) params.action = actionFilter;
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const data = await dmsApi.getAuditLogs(params);
      setLogs(data.logs || data.auditLogs || data || []);
      setTotal(data.total || data.totalCount || 0);
    } catch (err) {
      setError(err.error || err.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, actionFilter, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const exportCsv = () => {
    const headers = ['Timestamp', 'User', 'Action', 'Document', 'IP Address'];
    const rows = logs.map(l => [
      l.timestamp || l.createdAt || '',
      l.userName || (typeof l.userId === 'object' ? (l.userId?.name || l.userId?.email) : l.userId) || (typeof l.performedBy === 'object' ? (l.performedBy?.name || l.performedBy?.email) : l.performedBy) || '',
      l.action || '',
      l.documentTitle || (typeof l.documentId === 'object' ? (l.documentId?.title || l.documentId?.originalName) : l.documentId) || '',
      l.ipAddress || l.ip || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>Audit Log</Typography>
          <Typography variant="body2" color="text.secondary">Track all document activities across the system</Typography>
        </Box>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv} size="small">
          Export CSV
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Action Type</InputLabel>
          <Select value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); setPage(0); }} label="Action Type">
            <MenuItem value="">All Actions</MenuItem>
            {ACTIONS.map(a => <MenuItem key={a} value={a} sx={{ textTransform: 'capitalize' }}>{a.replace('_', ' ')}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          size="small" type="date" label="From" value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
          InputLabelProps={{ shrink: true }} sx={{ width: { xs: '100%', sm: 160 } }}
        />
        <TextField
          size="small" type="date" label="To" value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
          InputLabelProps={{ shrink: true }} sx={{ width: { xs: '100%', sm: 160 } }}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ bgcolor: MP.surface, border: `1px solid ${MP.border}` }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem', color: MP.textSecondary, borderColor: MP.border } }}>
                <TableCell>Timestamp</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Document</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell width={48}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6, color: MP.textSecondary }}>
                    No audit logs found
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log, i) => <AuditRow key={log._id || i} log={log} />)
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{ borderTop: `1px solid ${MP.border}` }}
          />
        </TableContainer>
      )}
    </Box>
  );
}
